import { useCallback, useEffect, useState } from 'react';
import { Cloud, AlertCircle, CheckCircle, RotateCw } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@ui/components/Button';
import { getDb } from '@data/db/database';
import { getSyncEngine, getSyncProvider } from '@data/sync/syncConfig';
import { readActiveBusinessId } from '@data/services/businessSession';
import type { SyncStatus } from '@domain/sync/retention';
import { cn } from '@ui/lib/cn';
import { useAuth } from '@platform/auth/authContext';

function useSyncStatus() {
  const queryClient = useQueryClient();
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  const activeBusinessId = useQuery({
    queryKey: ['sync', 'activeBusinessId'],
    queryFn: () => readActiveBusinessId(getDb() as never),
    staleTime: 60_000,
  });

  const provider = useQuery({
    queryKey: ['sync', 'provider'],
    queryFn: () => getSyncProvider(getDb() as never),
    staleTime: 60_000,
  });

  const syncStatusQuery = useQuery({
    queryKey: ['sync', 'status', activeBusinessId.data],
    queryFn: async () => {
      if (!activeBusinessId.data || !provider.data) return null;
      const engine = await getSyncEngine(getDb() as never);
      if (!engine) return null;
      return engine.status(activeBusinessId.data);
    },
    enabled: !!activeBusinessId.data && !!provider.data,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (syncStatusQuery.data) setSyncStatus(syncStatusQuery.data);
  }, [syncStatusQuery.data]);

  const runSync = useCallback(async () => {
    if (!activeBusinessId.data || busy) return;
    if (!user) {
      console.error('Manual sync failed: Not signed in.');
      return;
    }
    // Never sync a business that the authenticated user does not own in the
    // cloud. Switching Supabase accounts in the same browser can leave a
    // previous account's business selected locally; that must not be sent to
    // the cloud under the new user's credentials.
    const syncProvider = await getSyncProvider(getDb() as never);
    if (!syncProvider) {
      console.error('Manual sync failed: Sync not configured.');
      return;
    }
    const status = await syncProvider.getStatus();
    if (!status.authorisedBusinessIds.includes(activeBusinessId.data)) {
      console.error('Manual sync failed: This business is not linked to your DNR account.');
      return;
    }
    setBusy(true);
    try {
      const engine = await getSyncEngine(getDb() as never);
      if (!engine) throw new Error('Sync not configured');
      const result = await engine.run(activeBusinessId.data, 'manual');
      setSyncStatus(result.status);
      // Refresh immediately so the UI shows the post-sync state without
      // waiting for the next stale-window or interval poll.
      await queryClient.invalidateQueries({ queryKey: ['sync', 'status', activeBusinessId.data], refetchType: 'active' });
    } catch (error) {
      console.error('Manual sync failed:', error);
    } finally {
      setBusy(false);
    }
  }, [activeBusinessId.data, busy, queryClient, user]);

  return {
    syncStatus,
    provider: provider.data,
    activeBusinessId: activeBusinessId.data,
    busy: busy || syncStatusQuery.isFetching,
    runSync,
  };
}

export function SyncStatusButton() {
  const { syncStatus, provider, activeBusinessId, busy, runSync } = useSyncStatus();

  if (!provider || !activeBusinessId) return null;

  const isPending = syncStatus?.kind === 'pending';
  const isError = syncStatus?.kind === 'error';
  const isSynced = syncStatus?.kind === 'synced';
  const isConflict = syncStatus?.kind === 'conflict';

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={runSync}
        disabled={busy}
        loading={busy}
        loadingText="Syncing…"
        aria-label={busy ? 'Syncing…' : 'Sync now'}
        className={cn(
          'relative',
          isPending && 'text-amber-400',
          isError && 'text-red-400',
          isSynced && 'text-emerald-400',
          isConflict && 'text-amber-400',
        )}
      >
        {busy ? (
          <RotateCw className="h-4 w-4 animate-spin" aria-hidden />
        ) : isPending ? (
          <Cloud className="h-4 w-4 animate-pulse" aria-hidden />
        ) : isError ? (
          <AlertCircle className="h-4 w-4" aria-hidden />
        ) : isConflict ? (
          <AlertCircle className="h-4 w-4" aria-hidden />
        ) : (
          <CheckCircle className="h-4 w-4" aria-hidden />
        )}
      </Button>
      <span className="hidden sm:inline-block text-xs text-slate-400 min-w-[80px] text-right">
        {syncStatus?.failedCount && syncStatus.failedCount > 0
          ? `${syncStatus.failedCount} failed`
          : syncStatus?.pendingCount && syncStatus.pendingCount > 0
            ? `${syncStatus.pendingCount} pending`
            : isConflict
              ? `${syncStatus?.unresolvedConflictCount ?? 0} conflicts`
              : isPending
                ? 'Syncing…'
                : isError
                  ? 'Error'
                  : `Synced${syncStatus?.lastSyncOrigin === 'automatic' ? ' (auto)' : ''}`}
      </span>
    </div>
  );
}
